import { useState } from 'react';
import { Tabs } from 'radix-ui';
import { X } from 'lucide-react';
import { useUI, type Mode } from '@/state/store';
import { IconButton, PanelHeader, Placeholder, ScrollFade } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useLoader } from '@/features/loader/store';
import { SelectionPanel } from '@/features/loader/ui/panels/SelectionPanel';
import { SummaryPanel } from '@/features/loader/ui/panels/SummaryPanel';
import { WhatIfPanel } from '@/features/loader/ui/panels/WhatIfPanel';
import { SettingsPanel } from '@/features/loader/ui/panels/SettingsPanel';
import { AwarenessPanel } from '@/features/loader/ui/panels/AwarenessPanel';
import { TWIN_TABS, useTwinUi } from '@/features/twin/ui';
import { isRfims } from '@/features/rfims/registry';
import { RfimsEvidencePanel } from '@/features/rfims/EvidencePanel';
import { useTwin } from '@/features/twin/store';
import { useAssetName } from '@/features/twin/ui/hooks/useAssetName';
import { StoryPanel } from '@/features/overview/StoryPanel';
import { OperationsPanel } from '@/features/loader/ui/panels/OperationsPanel';
import { AosLosPanel } from '@/features/loader/ui/panels/AosLosPanel';
import { PassInspector } from '@/features/loader/passes/PassInspector';

const LOADER_TABS: Array<{ key: string; label: string; render: () => React.ReactNode }> = [
  { key: 'pass', label: 'Pass', render: () => <PassInspector /> },
  { key: 'status', label: 'Status', render: () => <OperationsPanel /> },
  { key: 'aoslos', label: 'AOS / LOS', render: () => <AosLosPanel /> },
  { key: 'selection', label: 'Selection', render: () => <SelectionPanel /> },
  { key: 'summary', label: 'Summary', render: () => <SummaryPanel /> },
  { key: 'whatif', label: 'What-if', render: () => <WhatIfPanel /> },
  { key: 'awareness', label: 'Awareness', render: () => <AwarenessPanel /> },
  { key: 'settings', label: 'Settings', render: () => <SettingsPanel /> },
];

/** Contextual tabs: the same asset shows different information per mode (Vir's
 *  specification from the February 2026 design review). */
const TABS: Record<Mode, Array<{ key: string; label: string; lines: string[] }>> = {
  overview: [
    {
      key: 'story',
      label: 'Story',
      lines: [
        'Data-flow stages with the elements in each',
        'Live packet counts per link',
        'Jump into any element',
      ],
    },
  ],
  operations: [
    {
      key: 'status',
      label: 'Status',
      lines: ['Tracking or idle', 'Current satellite contacts', 'Signal quality metrics'],
    },
    {
      key: 'contacts',
      label: 'Contacts',
      lines: ['Next passes with AOS and LOS', 'Per-spacecraft contact counts'],
    },
    { key: 'aoslos', label: 'AOS/LOS', lines: ['Event log filtered to the selection'] },
  ],
  maintenance: [
    {
      key: 'health',
      label: 'Health',
      lines: ['Subsystem cards with status roll-ups', 'Issue badges', 'Live readings with freshness'],
    },
    { key: 'readings', label: 'Readings', lines: ['History, graph, prediction, correlation'] },
    {
      key: 'procedures',
      label: 'Procedures',
      lines: ['Maintenance and data-flow procedures with camera choreography'],
    },
    { key: 'parts', label: 'Parts', lines: ['Spec sheets, service history, inventory and alternates'] },
  ],
  analysis: [
    { key: 'utilisation', label: 'Utilisation', lines: ['Hours used versus available', 'SLA status'] },
    {
      key: 'coverage',
      label: 'Coverage',
      lines: ['Coverage radius by altitude band', 'Gaps for this antenna or spacecraft'],
    },
    { key: 'history', label: 'History', lines: ['Contact history 24h, 7d, 30d'] },
  ],
  loader: [
    {
      key: 'details',
      label: 'Details',
      lines: ['Mission or antenna record', 'Requirements and capabilities'],
    },
    { key: 'layers', label: 'Layers', lines: ['Awareness layers, overlays and deep-space bodies'] },
    { key: 'whatif', label: 'What-if', lines: ['Toggle antennas and missions, re-run'] },
  ],
};

export type PanelPresentation = 'side' | 'sheet';

/** Side: a fixed 360 px column, so the content clips rather than reflows while the column animates. Sheet: full width. */
const frame = (presentation: PanelPresentation) =>
  cn('flex h-full flex-col', presentation === 'side' ? 'w-[360px]' : 'w-full');

const triggerClass = cn(
  'shrink-0 whitespace-nowrap rounded-t-control border-b-2 border-transparent px-1.5 py-1.5 text-xs text-ink-3 transition hover:text-ink focus-visible:outline-offset-[-2px]',
  'data-[state=active]:border-accent data-[state=active]:text-ink',
);

/** The tab row scrolls sideways (with fades) when the tabs do not fit the panel. */
function DetailTabs({
  tabs,
  active,
  label,
}: {
  tabs: Array<{ key: string; label: string }>;
  active: string;
  label: string;
}) {
  return (
    <ScrollFade className="shrink-0 border-b border-hairline" innerClassName="px-2 pt-2" activeKey={active}>
      <Tabs.List aria-label={label} className="flex gap-0.5">
        {tabs.map((t) => (
          <Tabs.Trigger key={t.key} value={t.key} className={triggerClass}>
            {t.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
    </ScrollFade>
  );
}

export function DetailPanel({
  mode,
  presentation = 'side',
}: {
  mode: Mode;
  presentation?: PanelPresentation;
}) {
  const selection = useUI((s) => s.selection);
  const clear = useUI((s) => s.clearSelection);
  const dataset = useLoader((s) => s.dataset);
  const [placeholderTab, setPlaceholderTab] = useState<{ mode: Mode; key: string } | null>(null);
  if (GLOBE_MODE_TABS[mode])
    return (
      <LoaderDetailPanel
        key={`${mode}:${dataset?.network === 'full_catalogue'}`}
        order={dataset?.network === 'full_catalogue' ? ['settings', 'awareness'] : GLOBE_MODE_TABS[mode]}
        presentation={presentation}
      />
    );
  if (mode === 'maintenance' && TWIN_TABS.length > 0) return <TwinDetailPanel presentation={presentation} />;
  if (mode === 'overview') return <OverviewDetailPanel presentation={presentation} />;
  const tabs = TABS[mode];
  const loaderTitle = selection.missionId
    ? (dataset?.missions.find((m) => m.id === selection.missionId)?.shortName ?? selection.missionId)
    : selection.antennaId
      ? (dataset?.antennas.find((a) => a.id === selection.antennaId)?.shortName ?? selection.antennaId)
      : null;
  const title = selection.assetId ?? loaderTitle ?? 'Nothing selected';
  const current = placeholderTab?.mode === mode ? placeholderTab.key : tabs[0].key;

  return (
    <div className={frame(presentation)}>
      <PanelHeader title={<span className="font-mono normal-case tracking-normal">{title}</span>}>
        {selection.assetId && (
          <IconButton label="Clear selection (Esc)" onClick={clear}>
            <X className="h-4 w-4" />
          </IconButton>
        )}
      </PanelHeader>
      <Tabs.Root
        value={current}
        onValueChange={(key) => setPlaceholderTab({ mode, key })}
        className="flex min-h-0 flex-1 flex-col"
      >
        <DetailTabs tabs={tabs} active={current} label="Detail tabs" />
        {tabs.map((t) => (
          <Tabs.Content key={t.key} value={t.key} className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
            <Placeholder title={t.label} lines={t.lines} />
          </Tabs.Content>
        ))}
      </Tabs.Root>
    </div>
  );
}

/** Tab order per globe mode: Operations leads with the live picture, Analysis with the numbers. */
const GLOBE_MODE_TABS: Partial<Record<Mode, string[]>> = {
  loader: ['status', 'pass', 'selection', 'summary', 'whatif', 'awareness', 'settings'],
  operations: ['status', 'selection', 'aoslos', 'awareness'],
  analysis: ['summary', 'selection', 'whatif', 'settings'],
};

function LoaderDetailPanel({ order, presentation }: { order: string[]; presentation: PanelPresentation }) {
  const tabs = order.map((key) => LOADER_TABS.find((t) => t.key === key)!).filter(Boolean);
  const selection = useUI((s) => s.selection);
  const clear = useUI((s) => s.clearSelection);
  const dataset = useLoader((s) => s.dataset);
  const title = selection.missionId
    ? (dataset?.missions.find((m) => m.id === selection.missionId)?.shortName ?? selection.missionId)
    : selection.antennaId
      ? (dataset?.antennas.find((a) => a.id === selection.antennaId)?.shortName ?? selection.antennaId)
      : 'Nothing selected';
  const hasSelection = Boolean(selection.missionId || selection.antennaId);
  const home = tabs[0].key;
  const [tab, setTab] = useState(
    hasSelection && tabs.some((t) => t.key === 'pass')
      ? 'pass'
      : hasSelection && tabs.some((t) => t.key === 'selection')
        ? 'selection'
        : home,
  );
  // A new selection brings its tab forward; clearing goes back to the summary.
  const selectionKey = `${selection.missionId ?? ''}:${selection.antennaId ?? ''}`;
  const [prevKey, setPrevKey] = useState(selectionKey);
  if (selectionKey !== prevKey) {
    setPrevKey(selectionKey);
    setTab(
      hasSelection && tabs.some((t) => t.key === 'pass')
        ? 'pass'
        : hasSelection && tabs.some((t) => t.key === 'selection')
          ? 'selection'
          : home,
    );
  }
  return (
    <div className={frame(presentation)}>
      <PanelHeader title={<span className="font-mono normal-case tracking-normal">{title}</span>}>
        {hasSelection && (
          <IconButton label="Clear selection (Esc)" onClick={clear}>
            <X className="h-4 w-4" />
          </IconButton>
        )}
      </PanelHeader>
      <Tabs.Root value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <DetailTabs tabs={tabs} active={tab} label="Detail tabs" />
        {tabs.map((t) => (
          <Tabs.Content key={t.key} value={t.key} className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
            {t.render()}
          </Tabs.Content>
        ))}
      </Tabs.Root>
    </div>
  );
}

/**
 * Maintenance mode: the twin panels, titled by the twin's own naming rule
 * (useAssetName: the asset spec's label, so a parametric antenna twin reads
 * "Wallops 26m" rather than its shared element's callsign, with the type and
 * callsign as the second line), or by the element and selection until an
 * asset is open.
 */
function TwinDetailPanel({ presentation }: { presentation: PanelPresentation }) {
  const selection = useUI((s) => s.selection);
  const clear = useUI((s) => s.clearSelection);
  const element = useTwin((s) => s.element);
  const tab = useTwinUi((s) => s.tab);
  const setTab = useTwinUi((s) => s.setTab);
  const name = useAssetName();
  const title = name?.title ?? element?.callsign ?? selection.assetId ?? 'Nothing selected';
  const subtitle = name?.subtitle ?? null;
  const tabs = isRfims(selection.assetId) ? [...TWIN_TABS.filter(t => ['health', 'readings'].includes(t.key)).map(t => t.key === 'health' ? { ...t, label: 'Telemetry' } : t), { key: 'evidence', label: 'Site & evidence', render: () => <RfimsEvidencePanel /> }] : TWIN_TABS;
  const activeTab = tabs.some(t => t.key === tab) ? tab : 'health';
  return (
    <div className={frame(presentation)}>
      <PanelHeader
        title={
          <span className="flex min-w-0 flex-col leading-tight" data-testid="detail-title">
            <span className="truncate font-mono normal-case tracking-normal">{title}</span>
            {subtitle && (
              <span className="truncate text-[10px] font-normal normal-case tracking-normal text-ink-3">
                {subtitle}
              </span>
            )}
          </span>
        }
      >
        {selection.assetId && (
          <IconButton label="Clear selection (Esc)" onClick={clear}>
            <X className="h-4 w-4" />
          </IconButton>
        )}
      </PanelHeader>
      <Tabs.Root value={activeTab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <DetailTabs tabs={tabs} active={activeTab} label="Twin tabs" />
        {tabs.map((t) => (
          <Tabs.Content key={t.key} value={t.key} className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
            {t.render()}
          </Tabs.Content>
        ))}
      </Tabs.Root>
    </div>
  );
}

/** Overview mode: the data-flow story. */
function OverviewDetailPanel({ presentation }: { presentation: PanelPresentation }) {
  return (
    <div className={frame(presentation)}>
      <PanelHeader title="Data flow" />
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        <StoryPanel />
      </div>
    </div>
  );
}
